// ASISTENTE «CLONAR CLASES» — arranque del ciclo nuevo.
//
// Es como Karla empieza cada semestre en Kydemy: al vencer el ciclo, clona las clases que
// continúan (se llevan sus alumnos, horario, profe y salón) y la original queda archivada.
// Sin esto no puede inscribir a nadie: necesita el "cascarón" del ciclo nuevo primero.
// Informe con la evidencia: letsmove/reportes/INFORME-CLONAR-CLASES-Y-VIGENCIA-20260729.md
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, CalendarRange, Users, Clock, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import {
  getCloneCandidates, cloneClasses, CloneCandidate, ClonePreview, CloneResult,
} from '../api';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { Skeleton } from '../../../components/Skeleton';
import BackButton from '../../../components/BackButton';
import { colorClase } from '../../../utils/classColor';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';
const money = (n: number) => `$${n.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
const fechaLarga = (ymd?: string | null) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const fechaCorta = (ymd?: string | null) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '—';
// '2026-08' → 'agosto 2026' (a Karla no le dice nada un mes en formato máquina).
const mesLargo = (ym?: string | null) =>
  ym ? new Date(`${ym}-01T00:00:00`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) : '';

function Opcion({ checked, onChange, title, hint }:
  { checked: boolean; onChange: (v: boolean) => void; title: string; hint: string }) {
  return (
    <label className="flex cursor-pointer select-none items-start gap-2.5 rounded-lg border border-line bg-surface px-3 py-2.5 transition hover:bg-surface-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--c-accent-solid))]" />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-content">{title}</span>
        <span className="block text-xs leading-snug text-content-3">{hint}</span>
      </span>
    </label>
  );
}

export default function CloneClassesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [cands, setCands] = useState<CloneCandidate[]>([]);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [opt, setOpt] = useState({
    copy_students: true, copy_waitlist: true, archive_original: true,
    rename_original: false, level_up: false, generate_debt: true,
  });
  const [debtDueDay, setDebtDueDay] = useState(3);
  const [preview, setPreview] = useState<ClonePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<CloneResult | null>(null);

  useEffect(() => {
    getCloneCandidates()
      .then((r) => {
        setCands(r.data);
        // Preselección: todas menos las que ya se clonaron (igual que Kydemy, que las trae marcadas).
        setSel(new Set(r.data.filter((c) => !c.clones.length).map((c) => c.class_id)));
        if (r.suggest) { setStartDate(r.suggest.start_date); setEndDate(r.suggest.end_date); }
      })
      .catch((e) => toast.error(e.message || 'No se pudieron cargar las clases'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cualquier cambio invalida la vista previa: no se ejecuta con números viejos.
  useEffect(() => { setPreview(null); }, [sel, startDate, endDate, opt, debtDueDay]);

  const totales = useMemo(() => {
    const s = cands.filter((c) => sel.has(c.class_id));
    return { clases: s.length, alumnos: s.reduce((t, c) => t + c.students, 0), espera: s.reduce((t, c) => t + c.waitlist, 0) };
  }, [cands, sel]);

  const debtMonth = startDate ? startDate.slice(0, 7) : '';
  const puedeSeguir = !!startDate && !!endDate && endDate >= startDate && sel.size > 0;

  const toggle = (id: number) =>
    setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function run(dry: boolean) {
    if (!puedeSeguir) { toast.error('Elige las fechas del ciclo y al menos una clase'); return; }
    setBusy(true);
    try {
      const r = await cloneClasses({
        class_ids: [...sel], start_date: startDate, end_date: endDate,
        ...opt, debt_month: debtMonth, debt_due_day: debtDueDay, dry_run: dry,
      });
      if (dry) setPreview(r as ClonePreview);
      else {
        setDone(r as CloneResult);
        toast.success(`${(r as CloneResult).classes} clases clonadas al ciclo nuevo`);
      }
    } catch (e: any) { toast.error(e.message || 'No se pudo completar el clonado'); }
    finally { setBusy(false); }
  }

  if (user?.role !== 'admin') {
    return <div className="p-6 text-sm text-content-2">Esta pantalla es solo para administración.</div>;
  }

  // ── Resultado final ──
  if (done) {
    return (
      <div className="mx-auto max-w-2xl p-4 sm:p-6">
        <div className="rounded-2xl border border-line bg-surface p-6 text-center">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
          <h1 className="mt-3 text-xl font-bold text-content">Ciclo nuevo creado</h1>
          <p className="mt-1 text-sm text-content-2">
            {done.classes} clases con {done.enrollments} inscripciones
            {done.waitlist > 0 && ` y ${done.waitlist} prospectos`}, del {fechaLarga(startDate)} al {fechaLarga(endDate)}.
          </p>
          {done.debts.count > 0 && (
            <p className="mt-2 text-sm text-content-2">
              Se generaron <strong>{done.debts.count} mensualidades</strong> de {mesLargo(done.debts.month)} por {money(done.debts.total)} en total.
            </p>
          )}
          <p className="mt-3 text-xs text-content-3">
            Las clases del ciclo anterior quedaron archivadas con su historial. Si algo salió mal, el
            respaldo para revertir está en <code className="text-[11px]">{done.log}</code>
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <button onClick={() => navigate('/clases')}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent">
              Ver el calendario
            </button>
            {done.debts.count > 0 && (
              <button onClick={() => navigate('/finanzas')}
                className="rounded-lg border border-line bg-surface px-4 py-2 text-sm font-medium text-content-2">
                Ver los adeudos
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <BackButton fallback="/clases" fallbackLabel="clases" />
      <header className="mb-5">
        <h1 className="flex items-center gap-2 text-xl font-bold text-content">
          <Copy size={20} className="text-content-3" /> Clonar clases al ciclo nuevo
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-content-2">
          Cada clase seleccionada se recrea con <strong>el mismo horario, profe, salón y sus alumnos</strong>,
          pero con las fechas del ciclo nuevo. La versión anterior se archiva y conserva su historial de
          asistencia y pagos. Quita el check a las clases que no continúan.
        </p>
      </header>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          {/* ── Columna izquierda: fechas + clases ── */}
          <div className="space-y-5">
            <section className="rounded-xl border border-line bg-surface p-4">
              <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-content">
                <CalendarRange size={16} className="text-content-3" /> Fechas del ciclo nuevo
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Inicia</label>
                  <input type="date" className={inputCls} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Termina</label>
                  <input type="date" className={inputCls} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </div>
              <p className="mt-2 text-xs text-content-3">
                Fuera de este rango las clases no aparecen en el calendario, así que las vacaciones no salen
                como días de clase ni generan faltas.
              </p>
            </section>

            <section className="rounded-xl border border-line bg-surface">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
                <h2 className="text-sm font-semibold text-content">
                  Clases que continúan <span className="text-content-3">({sel.size} de {cands.length})</span>
                </h2>
                <div className="flex gap-1.5 text-xs">
                  <button onClick={() => setSel(new Set(cands.map((c) => c.class_id)))}
                    className="rounded-lg border border-line px-2.5 py-1 text-content-2 hover:bg-surface-2">Todas</button>
                  <button onClick={() => setSel(new Set())}
                    className="rounded-lg border border-line px-2.5 py-1 text-content-2 hover:bg-surface-2">Ninguna</button>
                </div>
              </div>
              <ul className="divide-y divide-line">
                {cands.map((c) => {
                  const on = sel.has(c.class_id);
                  const yaClonada = c.clones.some((k) => k.start_date === startDate);
                  return (
                    <li key={c.class_id}>
                      <label className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition hover:bg-surface-2 ${on ? '' : 'opacity-55'}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle(c.class_id)}
                          className="h-4 w-4 shrink-0 accent-[rgb(var(--c-accent-solid))]" />
                        <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: colorClase(c.color) }} />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-content">{c.name}</span>
                            {yaClonada && (
                              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                                ya clonada a este ciclo
                              </span>
                            )}
                          </span>
                          <span className="block truncate text-xs text-content-3">
                            {c.teacher || 'sin profe'} · terminó {fechaCorta(c.end_date)} · {c.schedule_blocks} {c.schedule_blocks === 1 ? 'día' : 'días'} a la semana
                          </span>
                        </span>
                        <span className="shrink-0 text-right text-xs tabular-nums text-content-2">
                          <span className="flex items-center gap-1"><Users size={12} className="text-content-3" />{c.students}</span>
                          {c.waitlist > 0 && <span className="block text-[11px] text-content-3">+{c.waitlist} en espera</span>}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>

          {/* ── Columna derecha: opciones + preview + ejecutar ── */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-content">Qué se copia</h2>
              <Opcion checked={opt.copy_students} onChange={(v) => setOpt({ ...opt, copy_students: v })}
                title="Los alumnos inscritos" hint="Pasan a la clase nueva marcadas como pendientes de pago." />
              <Opcion checked={opt.copy_waitlist} onChange={(v) => setOpt({ ...opt, copy_waitlist: v })}
                title="La lista de espera" hint="Los prospectos que aún no se inscriben siguen en la clase nueva." />
              <Opcion checked={opt.archive_original} onChange={(v) => setOpt({ ...opt, archive_original: v })}
                title="Archivar la clase anterior" hint="Sale del calendario pero conserva asistencia y pagos del ciclo viejo." />
              <Opcion checked={opt.rename_original} onChange={(v) => setOpt({ ...opt, rename_original: v })}
                title="Marcar la anterior con su ciclo" hint="Le agrega al nombre, p. ej. «Ballet 1 (ene–jul 2026)»." />
              <Opcion checked={opt.level_up} onChange={(v) => setOpt({ ...opt, level_up: v })}
                title="Subir de nivel" hint="B1 pasa a B2. Las de nivel único (casi todas) no cambian." />
            </section>

            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-content">Cobranza</h2>
              <Opcion checked={opt.generate_debt} onChange={(v) => setOpt({ ...opt, generate_debt: v })}
                title={`Generar la mensualidad de ${debtMonth ? mesLargo(debtMonth) : 'el mes de inicio'}`}
                hint="Una por alumno (aunque esté en varias clases), con su tarifa y descuento reales." />
              {opt.generate_debt && (
                <div className="rounded-lg border border-line bg-surface px-3 py-2.5">
                  <label className={labelCls}>Vence el día</label>
                  <input type="number" min={1} max={28} className={inputCls} value={debtDueDay}
                    onChange={(e) => setDebtDueDay(Number(e.target.value))} />
                  <p className="mt-1 text-xs text-content-3">Del día 4 en adelante entra el pago tardío de $100.</p>
                </div>
              )}
            </section>

            {/* Resumen de lo seleccionado */}
            <section className="rounded-xl border border-line bg-surface-2/60 p-4 text-sm">
              <div className="flex items-center justify-between"><span className="text-content-2">Clases</span><strong className="tabular-nums text-content">{totales.clases}</strong></div>
              <div className="mt-1 flex items-center justify-between"><span className="text-content-2">Inscripciones</span><strong className="tabular-nums text-content">{opt.copy_students ? totales.alumnos : 0}</strong></div>
              <div className="mt-1 flex items-center justify-between"><span className="text-content-2">En espera</span><strong className="tabular-nums text-content">{opt.copy_waitlist ? totales.espera : 0}</strong></div>
            </section>

            {/* Vista previa exacta (la calcula el backend, sin escribir nada) */}
            {preview && (
              <section className="rounded-xl border border-brand-blue/40 bg-brand-blue/5 p-4 text-sm">
                <h3 className="flex items-center gap-1.5 font-semibold text-content"><Clock size={15} /> Vista previa</h3>
                <p className="mt-1.5 text-content-2">
                  Se crearán <strong>{preview.classes} clases</strong> ({preview.schedule_blocks} bloques de horario) con{' '}
                  <strong>{preview.enrollments} inscripciones</strong>
                  {preview.waitlist > 0 && <> y {preview.waitlist} prospectos</>}, del {fechaLarga(startDate)} al {fechaLarga(endDate)}.
                </p>
                {preview.debts && (
                  <p className="mt-2 text-content-2">
                    Mensualidades de {mesLargo(preview.debts.month)}: <strong>{preview.debts.students} alumnos</strong>,{' '}
                    <strong>{money(preview.debts.total)}</strong> en total, con vencimiento {fechaCorta(preview.debts.due_date)}.
                  </p>
                )}
                {!!preview.debts?.without_plan.length && (
                  <p className="mt-2 text-xs text-content-3">
                    Sin plan asignado, no se les genera adeudo ({preview.debts.without_plan.length}):{' '}
                    {preview.debts.without_plan.slice(0, 5).join(', ')}
                    {preview.debts.without_plan.length > 5 && ` y ${preview.debts.without_plan.length - 5} más`}.
                  </p>
                )}
                {!!preview.debts?.already_charged.length && (
                  <p className="mt-1 text-xs text-content-3">
                    Ya tenían la mensualidad del mes, no se duplica ({preview.debts.already_charged.length}).
                  </p>
                )}
                {!!preview.skipped.length && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    {preview.skipped.length} ya se habían clonado a este ciclo y se omiten: {preview.skipped.map((s) => s.name).join(', ')}.
                  </p>
                )}
              </section>
            )}

            <div className="space-y-2">
              {!preview ? (
                <button onClick={() => run(true)} disabled={!puedeSeguir || busy}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-4 py-2.5 text-sm font-semibold text-content transition hover:bg-surface-2 disabled:opacity-50">
                  {busy ? 'Calculando…' : <>Ver qué va a pasar <ArrowRight size={15} /></>}
                </button>
              ) : (
                <>
                  <button onClick={() => run(false)} disabled={busy || preview.classes === 0}
                    className="w-full rounded-lg bg-accent-solid px-4 py-2.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-50">
                    {busy ? 'Clonando…' : `Clonar ${preview.classes} ${preview.classes === 1 ? 'clase' : 'clases'}`}
                  </button>
                  <button onClick={() => setPreview(null)} disabled={busy}
                    className="w-full rounded-lg border border-line bg-surface px-4 py-2 text-xs font-medium text-content-2 hover:bg-surface-2">
                    Cambiar algo antes
                  </button>
                </>
              )}
              <p className="text-center text-[11px] leading-snug text-content-3">
                Todo queda respaldado: si algo sale mal se puede revertir con el registro que se guarda del clonado.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
